import { useEffect, useMemo, useRef } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@c2fo/liquidity';
import { AccountMultipleIcon } from '@c2fo/liquidity/icons';
import { useProfilesQuery } from '../../api/Profiles';
import { useServiceAccountSelector } from '../../contexts/FileDeliveryContext';
import { useUserDefaults } from '../../hooks';
import { useErrorState } from '../ErrorBanner';

export const ProfileSelect: React.FC = () => {
  const { serviceAccount, setServiceAccount, selectorRef, markInitialized } = useServiceAccountSelector();
  const { data: profiles = [], isLoading, isFetching } = useProfilesQuery();
  const { defaults, isLoaded } = useUserDefaults();
  const { addError } = useErrorState();
  const hasInitialized = useRef(false);

  const sortedProfiles = useMemo(() => {
    if (!profiles.length) return [];

    return [...profiles].sort((a, b) => {
      const aIsEpadmin = a.fdsServiceAccount.toLowerCase() === 'epadmin';
      const bIsEpadmin = b.fdsServiceAccount.toLowerCase() === 'epadmin';

      if (aIsEpadmin && !bIsEpadmin) return -1;
      if (!aIsEpadmin && bIsEpadmin) return 1;
      return a.fdsServiceAccount.localeCompare(b.fdsServiceAccount);
    });
  }, [profiles]);

  // Re-initialize when profiles change (e.g., datacenter changed) and current selection is invalid
  useEffect(() => {
    if (!isLoaded || sortedProfiles.length === 0 || isFetching) return;

    // If current selection is valid for the new profiles list, keep it
    if (serviceAccount && sortedProfiles.some((profile) => profile.fdsServiceAccount === serviceAccount)) {
      if (!hasInitialized.current) {
        hasInitialized.current = true;
        markInitialized();
      }

      return;
    }

    // Current selection is invalid or empty - need to select a new default

    hasInitialized.current = true;

    const userDefault = defaults.defaultServiceAccount;
    if (userDefault) {
      const isValid = sortedProfiles.some((profile) => profile.fdsServiceAccount === userDefault);
      if (isValid) {
        setServiceAccount(userDefault);
        markInitialized();
        return;
      }

      addError({
        id: 'default-service-account-warning',
        message: `Your saved default service account is no longer available. Using system default.`,
        type: 'warning',
      });
    }

    setServiceAccount(sortedProfiles[0].fdsServiceAccount);
    markInitialized();
  }, [
    isLoaded,
    serviceAccount,
    sortedProfiles,
    defaults.defaultServiceAccount,
    setServiceAccount,
    addError,
    markInitialized,
    isFetching,
  ]);

  return (
    <Select value={serviceAccount} onValueChange={setServiceAccount} disabled={isLoading}>
      <SelectTrigger ref={selectorRef} size="sm" className="w-52">
        <span className="flex items-center gap-2">
          <AccountMultipleIcon className="h-4 w-4 shrink-0" />
          <SelectValue placeholder={isLoading ? 'Loading...' : 'Select account'} />
        </span>
      </SelectTrigger>
      <SelectContent>
        {sortedProfiles.map((profile) => (
          <SelectItem key={profile.name} value={profile.fdsServiceAccount}>
            {profile.fdsServiceAccount}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
